import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import type { IPublicClientApplication, AccountInfo, SilentRequest } from '@azure/msal-browser';
import { AuthenticationError } from '~/shared/utils/auth';
import { clearAppPersistence } from '~/shared/utils/persistence';
import { loginRequest } from '~/services/auth-service';

const DEFAULT_TIMEOUT_MS = 30000;
const timeoutMs = Number(import.meta.env.VITE_API_TIMEOUT) || DEFAULT_TIMEOUT_MS;

const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: timeoutMs,
} as const;

/** Returns true if the error is an Axios timeout (no response within configured timeout). */
export function isTimeoutError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.code === 'ECONNABORTED';
}

let msalInstance: IPublicClientApplication | null = null;
let isLoggingOut = false;

/**
 * Single in-flight token refresh promise.
 * When multiple 401s arrive at the same time, only the first triggers
 * acquireTokenSilent; the rest await the same promise.
 */
let refreshPromise: Promise<string> | null = null;

export function configureMsalInstance(instance: IPublicClientApplication): void {
  msalInstance = instance;
  isLoggingOut = false;
}

function getPrimaryAccount(instance: IPublicClientApplication): AccountInfo | null {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) return activeAccount;
  const accounts = instance.getAllAccounts();
  return accounts[0] ?? null;
}

async function refreshIdToken(instance: IPublicClientApplication): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const account = getPrimaryAccount(instance);
      if (!account) throw new AuthenticationError('No account');

      const request: SilentRequest = {
        ...loginRequest,
        account,
        forceRefresh: true,
      };

      const response = await instance.acquireTokenSilent(request);
      if (!response.idToken) throw new AuthenticationError('No idToken in response');
      return response.idToken;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function getIdTokenSync(instance: IPublicClientApplication): string | null {
  try {
    const account = getPrimaryAccount(instance);
    return account?.idToken ?? null;
  } catch {
    return null;
  }
}

function forceLogout(): void {
  if (isLoggingOut) return;
  isLoggingOut = true;

  if (msalInstance) {
    try { msalInstance.setActiveAccount(null); } catch { /* ignore */ }
  }

  // Clear all persisted app state (filters, location, etc.) so next login is clean.
  try { clearAppPersistence(); } catch { /* ignore */ }

  // Clear MSAL session storage to fully remove all cached accounts/tokens.
  // MSAL is configured with cacheLocation: 'sessionStorage', so this
  // guarantees useMsal().accounts becomes empty.
  try { sessionStorage.clear(); } catch { /* ignore */ }

  // Hard-navigate to /signin to reset all React state and force a clean login.
  window.location.href = '/signin';
}

export const apiClient = axios.create(API_CONFIG);

/**
 * Extended config type used internally for retry tracking.
 */
type RetryableConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _retryServerError?: boolean;
};

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (isLoggingOut) {
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
      return config;
    }

    // Skip token injection for retried requests — the response interceptor
    // has already set the fresh token in the Authorization header.
    const retryConfig = config as RetryableConfig;
    if (retryConfig._retry || retryConfig._retryServerError) {
      return config;
    }

    if (!msalInstance) return config;

    const idToken = getIdTokenSync(msalInstance);
    if (idToken) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }

    return config;
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    if (isLoggingOut) return Promise.reject(error);
    if (!error.config || !error.response) return Promise.reject(error);

    const originalRequest = error.config as RetryableConfig;

    const errorMessage = error.response?.data && typeof error.response.data === 'object' && 'message' in error.response.data
      ? (error.response.data.message as string)?.toLowerCase() ?? ''
      : '';

    // ── Handle 502 Bad Gateway with retry ──
    const isServerError = error.response.status === 502;

    if (isServerError) {
      if (!originalRequest._retryServerError) {
        originalRequest._retryServerError = true;
        await new Promise(resolve => setTimeout(resolve, 500));
        return apiClient(originalRequest);
      }
      // Retry also returned 502 → force logout
      forceLogout();
      return Promise.reject(error);
    }

    // ── Handle 401 / token errors → immediate logout ──
    const isTokenError =
      error.response.status === 401 ||
      errorMessage.includes('invalid azure ad token') ||
      errorMessage.includes('invalid token') ||
      errorMessage.includes('token expired') ||
      errorMessage.includes('token missing') ||
      errorMessage.includes('session expired');

    if (isTokenError) {
      forceLogout();
      return Promise.reject(new AuthenticationError('Session expired. Redirecting to login.'));
    }

    return Promise.reject(error);
  }
);

